/**
 * Personal Gap Analysis Utility Functions
 * Helper functions for scoring, colors, and gap zone calculations
 */

import {
  GAP_ZONES,
  SCORE_TEXT_COLORS,
  SCORE_BADGE_COLORS,
  SCORE_DARK_COLORS,
  SCORE_LIGHT_BG_COLORS,
  CATEGORY_BACKGROUND_GRADIENTS
} from './personalGapAnalysisConstants';

/**
 * Get gap zone based on score
 * @param {number} score - Score percentage (0-100)
 * @returns {string} Gap zone key
 */
export const getGapZone = (score) => {
  // N/A questions (score 0) should not be in any gap zone
  if (score === 0) return 'n-a';

  // Score is 0-100 percentage, map to new scale
  if (score > 86) return 'no-gaps';              // >86% - Areas with no gaps (Score 5-6)
  if (score > 72) return 'minor-gaps';           // 72-86% - Areas with minor gaps (Score 4-5)
  if (score > 57) return 'considerable-gaps';    // 57-72% - Areas with considerable gaps (Score 3-4)
  if (score > 43) return 'critical-gaps';        // 43-57% - Areas with critical gaps (Score 2-3)
  if (score > 28) return 'very-critical-gaps';   // 28-43% - Areas with very critical gaps (Score 1-2)
  return 'extremely-critical';                   // >0 and <=28% - Areas with extremely critical gaps (Score 1)
};

/**
 * Get gap zone information
 * @param {string} zone - Gap zone key
 * @returns {Object} Zone information
 */
export const getGapZoneInfo = (zone) => {
  return GAP_ZONES[zone];
};

/**
 * Get score color based on score
 * @param {number} score - Score percentage (0-100)
 * @returns {string} Hex color code
 */
export const getScoreColor = (score) => {
  const zone = getGapZone(score);
  const zoneInfo = getGapZoneInfo(zone);
  return zoneInfo ? zoneInfo.color : '#6b7280'; // default to gray if no zone
};

/**
 * Get darker text color for score cards
 * @param {number} score - Score percentage (0-100)
 * @returns {string} Hex color code
 */
export const getScoreDarkColor = (score) => {
  const zone = getGapZone(score);
  return SCORE_DARK_COLORS[zone] || '#6b7280';
};

/**
 * Get light background color for score cards
 * @param {number} score - Score percentage (0-100)
 * @returns {string} Hex color code
 */
export const getScoreLightBg = (score) => {
  const zone = getGapZone(score);
  return SCORE_LIGHT_BG_COLORS[zone] || '#f9fafb';
};

/**
 * Get score text color class (Tailwind CSS)
 * @param {number} percent - Score percentage (0-100)
 * @returns {string} Tailwind CSS class
 */
export const getScoreTextColor = (percent) => {
  const zone = getGapZone(percent);
  return SCORE_TEXT_COLORS[zone] || 'text-gray-600';
};

/**
 * Get score badge color classes (Tailwind CSS)
 * @param {number} score - Score percentage (0-100)
 * @returns {string} Tailwind CSS classes
 */
export const getScoreBadgeColor = (score) => {
  const zone = getGapZone(score);
  return SCORE_BADGE_COLORS[zone] || 'bg-gray-100 text-gray-800 border-gray-300';
};

/**
 * Get category background color gradient (Tailwind CSS)
 * @param {number} score - Score percentage (0-100)
 * @returns {string} Tailwind CSS gradient classes
 */
export const getCategoryBackgroundColor = (score) => {
  const zone = getGapZone(score);
  return CATEGORY_BACKGROUND_GRADIENTS[zone] || 'bg-gradient-to-r from-gray-200 via-gray-100 to-gray-50';
};

/**
 * Get gap zone label text from score
 * @param {number} score - Score percentage (0-100)
 * @returns {string} Gap zone label
 */
export const getGapZoneLabel = (score) => {
  if (score > 86) return 'No Gaps';
  if (score > 72) return 'Minor Gaps';
  if (score > 57) return 'Considerable Gaps';
  if (score > 43) return 'Critical Gaps';
  if (score > 28) return 'Very Critical';
  return 'Extremely Critical';
};

/**
 * Group questions by gap zone
 * @param {Array} answeredQuestions - Array of answered questions with scores
 * @returns {Object} Questions grouped by gap zone
 */
export const getQuestionsByGapZone = (answeredQuestions) => {
  const zones = {
    'no-gaps': [],
    'minor-gaps': [],
    'considerable-gaps': [],
    'critical-gaps': [],
    'very-critical-gaps': [],
    'extremely-critical': [],
    'n-a': []
  };

  answeredQuestions.forEach(answer => {
    const zone = getGapZone(answer.score);
    if (zones[zone]) {
      zones[zone].push(answer);
    }
  });

  return zones;
};

/**
 * Get assessment type for a category
 * @param {string} catKey - Category key
 * @returns {Object|null} Assessment type info or null
 */
export const getAssessmentType = (catKey) => {
  // Import here to avoid circular dependency
  const { ASSESSMENT_TYPE_GROUPS } = require('./personalGapAnalysisConstants');

  for (const [type, group] of Object.entries(ASSESSMENT_TYPE_GROUPS)) {
    if (group.categories.includes(catKey)) {
      return { type, ...group };
    }
  }
  return null;
};

/**
 * Check if this is the first category in a group
 * @param {string} catKey - Category key
 * @param {Array} allCatKeys - All category keys (unused but kept for API compatibility)
 * @returns {boolean}
 */
export const isFirstInGroup = (catKey, allCatKeys) => {
  const assessmentType = getAssessmentType(catKey);
  if (!assessmentType) return false;
  const firstCatInGroup = assessmentType.categories[0];
  return catKey === firstCatInGroup;
};

/**
 * Check if this is the last category in a group
 * @param {string} catKey - Category key
 * @param {Array} allCatKeys - All category keys (unused but kept for API compatibility)
 * @returns {boolean}
 */
export const isLastInGroup = (catKey, allCatKeys) => {
  const assessmentType = getAssessmentType(catKey);
  if (!assessmentType) return false;
  const lastCatInGroup = assessmentType.categories[assessmentType.categories.length - 1];
  return catKey === lastCatInGroup;
};
